import { Request } from 'api';
import { AxiosResponse } from 'axios';
import {
    UpdateCommissionParams,
    GetCommissionsParams,
    CreateCommissionParams,
    CommissionBonusType,
    CompPlanReportParams,
    CompPlanSummary,
    CompPlanRecipient,
} from './commissions.types';
import { Commission } from '../../../networking/networking.types';

interface PaginatedApiResponse {
    data: Array<Commission>;
    links: any; // Replace 'any' with the actual type of 'links'
    meta: any; // Replace 'any' with the actual type of 'meta'
}

type ReturnTypeCollection = Promise<AxiosResponse<PaginatedApiResponse>>;

const getCommissionsForAdmins = (params?: GetCommissionsParams): ReturnTypeCollection => {
    console.log(params);
    const filterParams = {
        'filter[type]': params?.type,
        'filter[earned_date]': params?.earned_date,
        'filter[earned_date_from]': params?.earned_date_from,
        'filter[earned_date_to]': params?.earned_date_to,
        'filter[is_paid]': params?.is_paid,
        'filter[payout_uuid]': params?.payout_uuid,
        'filter[partner.organization]': params?.['partner.organization'],
        'filter[otherPartner.organization]': params?.['otherPartner.organization'],
        'filter[sellerPartner.organization]': params?.['sellerPartner.organization'],
        'filter[athlete_user_uuid]': params?.athlete_user_uuid,
        sort: params?.sort,
        page: params?.page,
        per_page: params?.per_page,
    };
    return Request.get(`admin/commissions`, { params: filterParams });
};

type ReturnTypeSingle = Promise<AxiosResponse<Commission>>;

const updateCommission = (
    commissionUuid: string,
    commissionData: UpdateCommissionParams,
): ReturnTypeSingle => {
    return Request.put(`admin/commissions/${commissionUuid}`, commissionData);
};

type ReturnTypeCommissionTypes = Promise<AxiosResponse<Array<string>>>;

const getCommissionTypesForAdmin = (): ReturnTypeCommissionTypes => {
    return Request.get(`admin/commissions/types`);
};

type ReturnTypeCommissionBonusTypes = Promise<AxiosResponse<Array<CommissionBonusType>>>;

const getCommissionBonusTypesForAdmin = (): ReturnTypeCommissionBonusTypes => {
    return Request.get(`admin/commissions/bonus-types`);
};

const adminCreateNewCommission = (commissionData: CreateCommissionParams): ReturnTypeSingle => {
    return Request.post(`admin/commissions`, commissionData);
};

/** Maps shared comp-plan filters to the backend's query string. */
const compPlanParams = (params?: CompPlanReportParams) => ({
    from: params?.from || undefined,
    to: params?.to || undefined,
    is_paid: params?.is_paid,
    organization_uuid: params?.organization_uuid || undefined,
    sort: params?.sort || undefined,
});

/** Headline KPIs, monthly trend, and spend-by-type — aggregated server-side. */
const getCompPlanSummary = (
    params?: CompPlanReportParams,
): Promise<AxiosResponse<CompPlanSummary>> => {
    return Request.get(`admin/commissions/comp-plan/summary`, { params: compPlanParams(params) });
};

/** Per-recipient leaderboard with YTD / trailing-12 / all-time paid totals. */
const getCompPlanRecipients = (
    params?: CompPlanReportParams,
): Promise<AxiosResponse<{ data: CompPlanRecipient[] }>> => {
    return Request.get(`admin/commissions/comp-plan/recipients`, {
        params: compPlanParams(params),
    });
};

/** Streams the filtered commission detail as a CSV blob for download. */
const getCompPlanExportCsv = (params?: CompPlanReportParams): Promise<AxiosResponse<Blob>> => {
    return Request.get(`admin/commissions/comp-plan/export`, {
        params: compPlanParams(params),
        responseType: 'blob',
    });
};

export {
    updateCommission,
    getCommissionsForAdmins,
    getCompPlanSummary,
    getCompPlanRecipients,
    getCompPlanExportCsv,
    getCommissionTypesForAdmin,
    getCommissionBonusTypesForAdmin,
    adminCreateNewCommission,
};
export type { UpdateCommissionParams, GetCommissionsParams, CreateCommissionParams };
